import { useState } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem, Divider,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box, Chip,
  useMediaQuery, useTheme, ListItem,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, BeachAccess, Inventory2,
  Event, AccountBalanceWallet, ReceiptLong, AssignmentReturned, CalendarMonth,
  MenuBook, Message as MessageIcon, Logout, Badge as BadgeIcon, OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { workspacePath } from '../utils/permissions';
import { useTeaching, resetTeachingCache } from '../hooks/useTeaching';

const DRAWER_W = 250;

// `soon` items render a "Coming soon" placeholder page — the module isn't built
// yet, but the nav slot is reserved so staff know it's on the way.
// `teacherOnly` items are hidden from non-teaching staff — only teachers
// (role classTeacher) and admins have lessons/subjects to see.
const NAV = [
  { label: 'Dashboard',           icon: <DashboardIcon />,        to: '/staff' },
  { label: 'My Teaching',         icon: <MenuBook />,             to: '/staff/teaching',    teacherOnly: true },
  { label: 'My Timetable',        icon: <CalendarMonth />,        to: '/staff/timetable',   teacherOnly: true },
  { label: 'My Leave',            icon: <BeachAccess />,          to: '/staff/leave' },
  { label: 'Store Requisitions',  icon: <Inventory2 />,           to: '/staff/requisitions' },
  { label: 'Messages',            icon: <MessageIcon />,          to: '/staff/messages' },
  { label: 'My Activities',       icon: <Event />,                to: '/staff/activities',  soon: true },
  { label: 'Imprest',             icon: <AccountBalanceWallet />, to: '/staff/imprest',     soon: true },
  { label: 'Claim Requisitions',  icon: <ReceiptLong />,          to: '/staff/claims',      soon: true },
  { label: 'Imprest Surrenders',  icon: <AssignmentReturned />,   to: '/staff/surrenders',  soon: true },
];

export default function StaffLayout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acctEl, setAcctEl] = useState(null);

  const user   = JSON.parse(localStorage.getItem('user') || '{}');
  const school = JSON.parse(localStorage.getItem('school') || '{}');
  const workspace = workspacePath();  // null for pure self-service staff

  // Only teaching staff have lessons & subjects. "Teaching" is decided by DATA
  // (subject allocations / class-teacher / stage-subject), not the coarse role —
  // so an English teacher whose role is `user` still sees My Teaching / Timetable.
  // Known roles reveal the items immediately; others reveal once the fetch lands.
  const { isTeaching } = useTeaching();
  const isTeachingStaff = ['classTeacher', 'admin'].includes(user.role) || isTeaching;
  const navItems = NAV.filter(item => !item.teacherOnly || isTeachingStaff);

  const go = (to) => { setMobileOpen(false); navigate(to); };

  const signOut = () => {
    resetTeachingCache();
    ['token', 'user', 'school', 'permissions'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  const isActive = (to) => to === '/staff'
    ? location.pathname === '/staff'
    : location.pathname.startsWith(to);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ gap: 1 }}>
        <BadgeIcon color="primary" />
        <Box>
          <Typography fontWeight={800} lineHeight={1.1}>Staff Portal</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {school.name || ''}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {navItems.map(item => (
          <ListItemButton key={item.to} onClick={() => go(item.to)}
            selected={isActive(item.to)}
            sx={{ borderRadius: 2, mb: 0.5,
              '&.Mui-selected': { bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '& .MuiListItemIcon-root': { color: 'white' } } }}>
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
            {item.soon && (
              <Chip label="Soon" size="small"
                sx={{ height: 18, fontSize: 10, bgcolor: isActive(item.to) ? 'rgba(255,255,255,0.25)' : 'grey.200' }} />
            )}
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        {workspace && (
          <ListItemButton onClick={() => go(workspace)} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><OpenInNew /></ListItemIcon>
            <ListItemText primary="Back to Workspace" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
          </ListItemButton>
        )}
        <ListItemButton onClick={signOut} sx={{ borderRadius: 2 }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary="Sign Out" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f4f6f8' }}>
      {/* Top bar */}
      <AppBar position="fixed" elevation={1}
        sx={{ zIndex: theme.zIndex.drawer + 1, bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(o => !o)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/staff')} flex={1}>
            <Typography fontWeight={800} color="primary" lineHeight={1.1}>
              {school.name || 'Staff Portal'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Staff Portal</Typography>
          </Box>

          {/* Account */}
          <IconButton onClick={(e) => setAcctEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 700 }}>
              {(user.fullName || user.email || '?').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={acctEl} open={Boolean(acctEl)} onClose={() => setAcctEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <ListItem sx={{ pt: 0.5, pb: 1 }}>
              <Box>
                <Typography fontWeight={700} variant="body2">{user.fullName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              </Box>
            </ListItem>
            <Divider />
            <MenuItem onClick={() => { setAcctEl(null); navigate('/staff'); }}>
              <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon> Dashboard
            </MenuItem>
            {workspace && (
              <MenuItem onClick={() => { setAcctEl(null); navigate(workspace); }}>
                <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon> Back to Workspace
              </MenuItem>
            )}
            <MenuItem onClick={signOut} sx={{ color: 'error.main' }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Side menu — permanent on desktop, swipe-in on mobile */}
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_W, flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_W, boxSizing: 'border-box', borderRight: '1px solid #eee' },
        }}>
        {drawerContent}
      </Drawer>

      {/* Page content */}
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_W}px)` } }}>
        <Toolbar /> {/* offset for the fixed AppBar */}
        {children}
      </Box>
    </Box>
  );
}
